/*
 * Copyright (c) 2025 - 2026. Bolmate B.V.
 */
import './style/reviewNotifications.scss';
import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {NotificationType} from '../../../util/enums';
import {PortalSelectOption, PortalSelectValue, Rating} from '../../../util/types';
import Notification from '../../../models/notificationModel';
import {AwesomeIcon, showSnackbar} from '@dvrd/dvr-controls';
import {renderPlural} from '../../../util/utils';
import BolRating from '../../bolRating/bolRating';
import classNames from 'classnames';
import PortalSelectController from '../../controls/portalSelect/portalSelectController';
import {NotificationProps, NotificationTypeProps} from '../notificationsPage';
import useDefaultNotification from '../useDefaultNotification';
import NotificationProducts from '../notificationComponents/notificationProducts';
import NotificationActions from '../notificationComponents/notificationActions';
import NotificationChannelsSelect from '../notificationComponents/notificationChannelsSelect';
import NotificationIntervalComp from '../notificationComponents/notificationInterval';
import NotificationChannelsBadge from '../notificationComponents/notificationChannelsBadge';

const RATINGS: Array<Rating> = [1, 2, 3, 4, 5];

function ReviewNotification(props: NotificationProps) {
    const {t} = useTranslation();
    const {
        notification,
        onChangeNotification,
        loading,
        onDeleteNotification,
        onSendTestNotification,
        disabled,
        canDelete,
        canEdit,
        onReplaceNotification
    } = props;
    const [onChangeEmailAddresses] = useDefaultNotification(props);
    // Bumped to force a re-render with a fresh (unchanged) value array, which
    // the controlled PortalSelect syncs back into its internal state — this is
    // how a REJECTED change (empty selection) visually snaps back.
    const [, setSyncNonce] = useState(0);

    const ratingItems: Array<PortalSelectOption<Rating>> = useMemo(
        () => RATINGS.map(rating => ({value: rating, label: <BolRating value={rating} staticStars />})),
        []
    );

    function parseRatings(value: PortalSelectValue<Rating>): string {
        const selected = Array.isArray(value) ? value : [];
        if (!selected.length || selected.length === 5) return t('notifications.reviews.allScores', 'Alle scores');
        if (selected.length === 1) {
            const rating = selected[0];
            return `${rating} ${renderPlural(rating, t('notifications.reviews.starSingular', 'ster'), t('notifications.reviews.starPlural', 'sterren'))}`;
        }
        return `${selected.length} ${t('notifications.reviews.scoresSuffix', 'scores')}`;
    }

    function onChangeRatings(value: Rating | null, _selected: boolean, values?: Array<Rating>) {
        let next = values ?? [];
        // The selection may never end up empty. The "Wis"/Clear header control
        // (value === null) collapses to the minimum allowed selection — one
        // score; unchecking the last individual score is rejected instead.
        if (!next.length) {
            if (value === null) {
                next = [RATINGS[0]];
            } else {
                showSnackbar(t('notifications.reviews.minOneScore', 'Kies minimaal één score'), {
                    backgroundColor: 'color-blue-bolmate',
                    textColor: 'white'
                });
                setSyncNonce(nonce => nonce + 1);
                return;
            }
        }
        const config = notification.reviewConfig;
        config.ratings.clear();
        next.forEach(rating => config.ratings.add(rating));
        notification.flex = config.dump();
        onChangeNotification(notification);
        // Re-render with the persisted value so the controlled PortalSelect
        // syncs its internal state (Clear left it empty).
        setSyncNonce(nonce => nonce + 1);
    }

    return (
        <div className={classNames('review-notification notification-config', disabled && 'disabled')}>
            <label className='notification-title'>
                <AwesomeIcon name='star' className='type-icon' />{' '}
                {t('notifications.reviews.title', 'Review notificatie')}{' '}
                <NotificationChannelsBadge notification={notification} />
            </label>
            <div className='notification-card'>
                <div className='notification'>
                    <section>
                        <label className='fixed-text'>
                            {t('notifications.reviews.newReviewsWith', 'Bij nieuwe reviews met')}
                        </label>
                        <PortalSelectController
                            value={Array.from(notification.reviewConfig.ratings)}
                            onChange={onChangeRatings}
                            items={ratingItems}
                            parseSelectValue={parseRatings}
                            disabled={loading || disabled || !canEdit}
                            multiple
                            controls={['selectAll', 'deselectAll']}
                            size='medium'
                            outlined
                        />
                    </section>
                    <NotificationProducts
                        onSuccess={onReplaceNotification}
                        notification={notification}
                        disabled={loading || disabled || !canEdit}
                    />
                    <NotificationChannelsSelect
                        notification={notification}
                        disabled={loading || disabled || !canEdit}
                        onChangeEmailAddresses={onChangeEmailAddresses}
                        onReplaceNotification={onReplaceNotification}
                    />
                    <NotificationIntervalComp
                        onChangeNotification={onChangeNotification}
                        disabled={loading || disabled || !canEdit}
                        notification={notification}
                    />
                </div>
                <NotificationActions
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    disabled={disabled}
                    notification={notification}
                    canDelete={canDelete}
                />
            </div>
        </div>
    );
}

export default function ReviewNotifications(props: NotificationTypeProps) {
    const {notifications} = props;
    const reviewNotifications = useMemo(() => {
        return notifications.filter(
            (notification: Notification) => notification.notificationType === NotificationType.REVIEWS
        );
    }, [notifications]);
    if (!reviewNotifications.length) return null;

    function renderNotification(notification: Notification) {
        return <ReviewNotification key={notification.id} notification={notification} disabled={false} {...props} />;
    }

    return (
        <div className='notification-type review-notifications'>
            <div className='notifications'>{reviewNotifications.map(renderNotification)}</div>
        </div>
    );
}
